import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useCreateMedicine, useUpdateMedicine } from '@/api/hooks/useMedicine';
import { useStoreSettings } from '@/api/hooks/useSettings';
import { useSuppliers } from '@/api/hooks/useSuppliers';
import { GST_RATES, MEDICINE_CATEGORIES, MEDICINE_UNITS } from '@/api/inventory';
import type { Medicine, MedicineCategory } from '@/api/types';
import { useSession } from '@/auth/session';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';

interface FormState {
  name: string;
  shortName: string;
  genericName: string;
  composition: string;
  category: MedicineCategory;
  unit: string;
  stock: string;
  minStock: string;
  costPrice: string;
  price: string;
  gstRate: string;
  batchNo: string;
  expiryDate: string;
  location: string;
  supplierId: string;
  barcode: string;
}

const EMPTY: FormState = {
  name: '',
  shortName: '',
  genericName: '',
  composition: '',
  category: 'Other',
  unit: 'Tablets',
  stock: '0',
  minStock: '10',
  costPrice: '0',
  price: '0',
  gstRate: '12',
  batchNo: '',
  expiryDate: '',
  location: '',
  supplierId: '',
  barcode: '',
};

function toFormState(medicine: Medicine): FormState {
  return {
    name: medicine.name,
    shortName: medicine.shortName ?? '',
    genericName: medicine.genericName ?? '',
    composition: medicine.composition ?? '',
    category: medicine.category,
    unit: medicine.unit,
    stock: String(medicine.stock),
    minStock: String(medicine.minStock),
    costPrice: medicine.costPrice != null ? String(medicine.costPrice) : '0',
    price: String(medicine.price),
    gstRate: String(medicine.gstRate),
    batchNo: medicine.batchNo,
    expiryDate: medicine.expiryDate.split('T')[0] ?? '',
    location: medicine.location ?? '',
    supplierId: medicine.supplierId,
    barcode: medicine.barcode,
  };
}

/**
 * Create and edit share one form. On create, opening stock becomes a batch
 * server-side so FEFO has something to draw from on the first sale. On edit,
 * changing stock is treated as a manual adjustment — restocking with a new
 * batch has its own flow (RestockDialog), because a bare number can't say
 * which batch gained the units.
 */
export function MedicineFormDialog({
  medicine,
  open,
  onOpenChange,
}: {
  /** Undefined opens the dialog in "create" mode. */
  medicine?: Medicine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const isEdit = medicine !== undefined;
  const { can } = useSession();
  const canViewCosts = can(PERMISSIONS.medicinesViewCosts);

  const { data: suppliers } = useSuppliers();
  const { data: storeSettings } = useStoreSettings();
  const createMedicine = useCreateMedicine();
  const updateMedicine = useUpdateMedicine();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // New medicines start at the pharmacy's configured "Default GST %"
      // (Settings), not a hardcoded slab — otherwise that setting has no
      // effect and every new medicine silently gets whatever was hardcoded
      // here, regardless of what the store actually charges.
      const defaultGst = storeSettings && GST_RATES.includes(storeSettings.defaultGst)
        ? String(storeSettings.defaultGst)
        : EMPTY.gstRate;
      setForm(medicine ? toFormState(medicine) : { ...EMPTY, gstRate: defaultGst });
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, medicine?.id, storeSettings]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const submitting = createMedicine.isPending || updateMedicine.isPending;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.batchNo.trim() || !form.expiryDate || !form.supplierId) {
      setError('Name, batch number, expiry date, and supplier are required.');
      return;
    }

    const payload = {
      name: form.name.trim(),
      shortName: form.shortName.trim() || undefined,
      genericName: form.genericName.trim() || undefined,
      composition: form.composition.trim() || undefined,
      category: form.category,
      unit: form.unit,
      stock: Math.max(0, Number(form.stock) || 0),
      minStock: Math.max(0, Number(form.minStock) || 0),
      costPrice: Math.max(0, Number(form.costPrice) || 0),
      price: Math.max(0, Number(form.price) || 0),
      gstRate: Number(form.gstRate),
      batchNo: form.batchNo.trim(),
      expiryDate: form.expiryDate,
      location: form.location.trim() || undefined,
      supplierId: form.supplierId,
      barcode: form.barcode.trim() || undefined,
    };

    try {
      if (isEdit) {
        await updateMedicine.mutateAsync({ id: medicine.id, request: payload });
        toast({ title: 'Medicine updated', variant: 'success' });
      } else {
        await createMedicine.mutateAsync(payload);
        toast({ title: 'Medicine added', variant: 'success' });
      }
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'PLAN_LIMIT') {
          setError(err.message);
        } else if (err.errors) {
          setError(Object.values(err.errors).flat()[0] ?? err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit medicine' : 'Add medicine'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="f-name">Brand name *</Label>
            <Input
              id="f-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Crocin 500mg"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="f-shortname">Short name (billing display)</Label>
              <Input
                id="f-shortname"
                value={form.shortName}
                onChange={(e) => set('shortName', e.target.value)}
                placeholder="e.g. Crocin 500"
                maxLength={40}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-generic">Generic name</Label>
              <Input
                id="f-generic"
                value={form.genericName}
                onChange={(e) => set('genericName', e.target.value)}
                placeholder="e.g. Paracetamol"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="f-composition">Composition / salt (for substitute suggestions)</Label>
            <Input
              id="f-composition"
              value={form.composition}
              onChange={(e) => set('composition', e.target.value)}
              placeholder="e.g. Paracetamol 500mg"
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('category')} *</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v as MedicineCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEDICINE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Unit *</Label>
              <Select value={form.unit} onValueChange={(v) => set('unit', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEDICINE_UNITS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="f-stock">
                {isEdit ? 'Stock (manual adjustment)' : 'Opening stock *'}
              </Label>
              <Input
                id="f-stock"
                type="number"
                min={0}
                value={form.stock}
                onChange={(e) => set('stock', e.target.value)}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  To record a delivery instead, use Restock — it keeps batch and expiry tracking accurate.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-min">Min stock (low-stock alert threshold)</Label>
              <Input
                id="f-min"
                type="number"
                min={0}
                value={form.minStock}
                onChange={(e) => set('minStock', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {canViewCosts && (
              <div className="space-y-1.5">
                <Label htmlFor="f-cost">Cost price (₹)</Label>
                <Input
                  id="f-cost"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.costPrice}
                  onChange={(e) => set('costPrice', e.target.value)}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="f-price">Selling price (₹) *</Label>
              <Input
                id="f-price"
                type="number"
                min={0}
                step="0.01"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>GST rate</Label>
              <Select value={form.gstRate} onValueChange={(v) => set('gstRate', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GST_RATES.map((rate) => (
                    <SelectItem key={rate} value={String(rate)}>
                      {rate}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">What's actually charged on this medicine's bills.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-batch">{t('batchNo')} *</Label>
              <Input
                id="f-batch"
                value={form.batchNo}
                onChange={(e) => set('batchNo', e.target.value)}
                placeholder="e.g. BT2024001"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="f-expiry">{t('expiryDate')} *</Label>
              <Input
                id="f-expiry"
                type="date"
                value={form.expiryDate}
                onChange={(e) => set('expiryDate', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-location">{t('location')} / shelf</Label>
              <Input
                id="f-location"
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder="e.g. Rack A-2"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('supplier')} *</Label>
            <Select value={form.supplierId} onValueChange={(v) => set('supplierId', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="f-barcode">Barcode / EAN (optional)</Label>
            <Input
              id="f-barcode"
              value={form.barcode}
              onChange={(e) => set('barcode', e.target.value)}
              placeholder="Scan or type the barcode from packaging"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Used by the billing scanner. Leave empty if not needed.
            </p>
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="animate-spin" />}
              {isEdit ? 'Save changes' : t('addMedicine')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
